import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { HeartHandshake, MessageSquare, X, Flame } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

// List-view prayer surfaces: streak badge, partner card + picker, leaderboard.
const PrayerPanels = ({
  streak, partner, leaderboard,
  partnerPickerOpen, setPartnerPickerOpen, partnerPeople, openPartnerPicker,
  invitePartner, respondPartner, endPartner, messagePartner,
  toggleLeaderboardPrivacy, leaderboardRef,
}) => {
  const { t } = useLanguage();
  return (
  <>
    {streak && (streak.current_streak > 0 || streak.prayed_today) && (
      <div className="mx-3 mt-3 flex items-center gap-2.5 rounded-xl bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-amber-500/30 px-3 py-2" data-testid="prayer-streak-badge">
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white shrink-0">
          <Flame className="w-5 h-5" />
        </div>
        <div className="leading-tight">
          <p className="text-sm font-semibold" data-testid="prayer-streak-count">
            {streak.current_streak} {streak.current_streak === 1 ? t('messages.prayerStreakOne') : t('messages.prayerStreakOther')}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {streak.prayed_today
              ? t('messages.prayedToday')
              : t('messages.prayToKeepGoing')}
            {streak.longest_streak > streak.current_streak && (
              <span> · {t('messages.best')}: {streak.longest_streak}</span>
            )}
            {streak.freeze_available && (
              <span className="text-sky-500"> · ❄️ {t('messages.freezeReady')}</span>
            )}
          </p>
        </div>
      </div>
    )}
    <div className="mx-3 mt-3 rounded-xl border bg-gradient-to-r from-rose-500/10 to-purple-500/10 border-purple-500/30 p-3" data-testid="prayer-partner-card">
      {!partner && (
        <div className="flex items-center gap-2.5">
          <HeartHandshake className="w-5 h-5 text-purple-500 shrink-0" />
          <p className="text-sm flex-1">{t('messages.prayWithPartner')}</p>
          <Button size="sm" variant="outline" onClick={openPartnerPicker} data-testid="partner-invite-btn">
            {t('messages.invite')}
          </Button>
        </div>
      )}
      {partner?.incoming && (
        <div className="flex items-center gap-2.5" data-testid="partner-incoming">
          <Avatar className="w-9 h-9">{partner.partner_picture ? <AvatarImage src={partner.partner_picture} /> : null}<AvatarFallback className="text-xs bg-purple-500/15">{(partner.partner_name || '?').slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
          <p className="text-sm flex-1 leading-tight"><span className="font-semibold">{partner.partner_name}</span> {t('messages.invitedYouPartner')}</p>
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => respondPartner(true)} data-testid="partner-accept-btn">{t('messages.accept')}</Button>
          <button onClick={() => respondPartner(false)} className="text-muted-foreground hover:text-foreground" data-testid="partner-decline-btn"><X className="w-4 h-4" /></button>
        </div>
      )}
      {partner?.outgoing && (
        <div className="flex items-center gap-2.5" data-testid="partner-outgoing">
          <HeartHandshake className="w-5 h-5 text-purple-500 shrink-0" />
          <p className="text-sm flex-1">{t('messages.inviteSentTo', { name: partner.partner_name })}</p>
          <Button size="sm" variant="ghost" onClick={endPartner} data-testid="partner-cancel-btn">{t('messages.cancel')}</Button>
        </div>
      )}
      {partner?.status === 'active' && (
        <div className="flex items-center gap-3" data-testid="partner-active">
          <div className="flex items-center justify-center w-11 h-11 rounded-full bg-gradient-to-br from-rose-500 to-purple-500 text-white shrink-0"><HeartHandshake className="w-5 h-5" /></div>
          <div className="flex-1 leading-tight">
            <p className="text-sm font-semibold">{t('messages.sharedStreak')}: 🔥 {partner.shared_streak}
              {partner.snoozed_today && (
                <span className="ml-2 inline-flex items-center rounded-full bg-sky-500/15 text-sky-600 dark:text-sky-400 px-2 py-0.5 text-[10px] font-medium align-middle" data-testid="partner-snoozed-chip">
                  💤 {t('messages.snoozedToday')}
                </span>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground">{t('messages.with')} {partner.partner_name} · {t('messages.bothPrayToday')}</p>
          </div>
          <Button size="sm" variant="outline" className="h-8 gap-1" onClick={messagePartner} data-testid="partner-message-btn">
            <MessageSquare className="w-3.5 h-3.5" />{t('messages.message')}
          </Button>
          <button onClick={endPartner} className="text-[11px] text-muted-foreground hover:text-foreground underline" data-testid="partner-end-btn">{t('messages.end')}</button>
        </div>
      )}
    </div>
    <Dialog open={partnerPickerOpen} onOpenChange={setPartnerPickerOpen}>
      <DialogContent className="max-w-sm" data-testid="partner-picker-dialog">
        <DialogTitle>{t('messages.choosePartner')}</DialogTitle>
        <DialogDescription>{t('messages.invitePartnerDesc')}</DialogDescription>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {partnerPeople.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{t('messages.addFriendsFirst')}</p>
          ) : partnerPeople.map((p) => (
            <button key={p.user_id} onClick={() => { setPartnerPickerOpen(false); invitePartner(p.user_id); }} className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-muted text-left" data-testid={`partner-pick-${p.user_id}`}>
              <Avatar className="w-8 h-8">{p.picture ? <AvatarImage src={p.picture} /> : null}<AvatarFallback className="text-[10px] bg-primary/15">{(p.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
              <span className="text-sm">{p.name}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>

    {(leaderboard.length > 0 || streak?.opt_out) && (
      <div ref={leaderboardRef} className="mx-3 mt-3 rounded-xl border bg-card p-3" data-testid="streak-leaderboard">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-orange-600 flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" /> {t('messages.topPrayerStreaks')}</p>
          <button onClick={toggleLeaderboardPrivacy} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="leaderboard-privacy-toggle">
            {streak?.opt_out ? t('messages.showMe') : t('messages.hideMe')}
          </button>
        </div>
        {leaderboard.length === 0 ? (
          <p className="text-xs text-muted-foreground py-1" data-testid="leaderboard-empty">
            {streak?.opt_out
              ? t('messages.hiddenRejoin')
              : t('messages.noActiveStreaks')}
          </p>
        ) : (
          <div className="space-y-1.5">
            {leaderboard.map((u, i) => (
              <div key={u.user_id} className="flex items-center gap-2.5" data-testid={`leaderboard-row-${i}`}>
                <span className={`w-5 text-center text-sm font-bold ${i === 0 ? 'text-amber-500' : i === 1 ? 'text-slate-400' : i === 2 ? 'text-amber-700' : 'text-muted-foreground'}`}>{i + 1}</span>
                <Avatar className="w-7 h-7">
                  {u.picture ? <AvatarImage src={u.picture} /> : null}
                  <AvatarFallback className="text-[10px] bg-primary/15">{(u.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="text-sm flex-1 truncate">{u.name}</span>
                <span className="text-sm font-semibold text-orange-600">🔥 {u.current_streak}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )}
  </>
  );
};

export default PrayerPanels;
